import { useRef, useState, type CSSProperties } from 'react'
import { PexIcon } from '../../../components/icons/PexIcon'
import { useT } from '../../../i18n'
import { messagingI18n } from '../../../i18n/messaging'

export type VoiceMessagePlayerProps = {
  src: string
  label: string
  appearance: 'default' | 'messenger'
  downloadUrl?: string
  durationHintSeconds?: number
}

function formatTime(seconds: number): string {
  const safeSeconds = Number.isFinite(seconds) ? Math.max(0, seconds) : 0
  const wholeSeconds = Math.floor(safeSeconds)
  return `${Math.floor(wholeSeconds / 60)}:${String(wholeSeconds % 60).padStart(2, '0')}`
}

function playbackWasInterrupted(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError'
}

export function VoiceMessagePlayer({
  src,
  durationHintSeconds = 0,
  ...props
}: VoiceMessagePlayerProps) {
  return (
    <VoiceMessagePlayerSession
      key={`${src}:${durationHintSeconds}`}
      src={src}
      durationHintSeconds={durationHintSeconds}
      {...props}
    />
  )
}

function VoiceMessagePlayerSession({
  src,
  label,
  appearance,
  downloadUrl,
  durationHintSeconds = 0,
}: VoiceMessagePlayerProps) {
  const t = useT(messagingI18n)
  const audioRef = useRef<HTMLAudioElement>(null)
  const resolvingInfiniteDurationRef = useRef(false)
  const [playing, setPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(durationHintSeconds)
  const [playbackFailed, setPlaybackFailed] = useState(false)

  const togglePlayback = async () => {
    const audio = audioRef.current
    if (!audio) return

    if (playing) {
      audio.pause()
      return
    }

    setPlaybackFailed(false)
    try {
      await audio.play()
    } catch (error) {
      setPlaying(false)
      if (!playbackWasInterrupted(error)) setPlaybackFailed(true)
    }
  }

  const syncDuration = () => {
    const audio = audioRef.current
    const nextDuration = audio?.duration
    if (nextDuration !== undefined && Number.isFinite(nextDuration)) {
      setDuration(Math.max(0, nextDuration))
      if (resolvingInfiniteDurationRef.current && audio) {
        resolvingInfiniteDurationRef.current = false
        audio.currentTime = 0
        setCurrentTime(0)
      }
    } else if (
      nextDuration === Number.POSITIVE_INFINITY &&
      audio &&
      !resolvingInfiniteDurationRef.current
    ) {
      // MediaRecorder WebM files omit container duration. Seeking once lets
      // Chromium resolve the finite duration without starting playback.
      resolvingInfiniteDurationRef.current = true
      audio.currentTime = Number.MAX_SAFE_INTEGER
    }
  }

  const syncCurrentTime = () => {
    const nextTime = audioRef.current?.currentTime
    if (nextTime !== undefined && Number.isFinite(nextTime)) {
      setCurrentTime(Math.max(0, nextTime))
    }
  }

  const markPlayable = () => {
    setPlaybackFailed(false)
  }

  const handleMediaError = () => {
    const mediaErrorCode = audioRef.current?.error?.code
    // MEDIA_ERR_ABORTED is a source/playback transition, not corrupt media.
    // The browser can subsequently load the same Blob and emit `canplay`.
    if (mediaErrorCode !== undefined && mediaErrorCode !== 1) {
      setPlaybackFailed(true)
    }
  }

  const seek = (nextTime: number) => {
    const audio = audioRef.current
    if (!audio || !Number.isFinite(nextTime)) return

    const boundedTime = Math.min(Math.max(0, nextTime), duration)
    audio.currentTime = boundedTime
    setCurrentTime(boundedTime)
  }

  const boundedCurrentTime = Math.min(currentTime, duration || currentTime)
  const progress = duration > 0 ? (boundedCurrentTime / duration) * 100 : 0
  const isMessenger = appearance === 'messenger'

  return (
    <div
      role="group"
      aria-label={label}
      data-appearance={appearance}
      className="w-full min-w-0 max-w-[20rem] font-[inherit]"
    >
      <div
        className={`flex min-w-0 items-center gap-2 rounded-[var(--radius-control)] border px-2 py-1.5 ${
          isMessenger
            ? 'border-white/10 bg-white/[0.06] text-white'
            : 'border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)]'
        }`}
      >
        <button
          type="button"
          aria-label={
            playing ? t.media.pauseVoiceMessage : t.media.playVoiceMessage
          }
          onClick={() => void togglePlayback()}
          className={`inline-flex size-11 shrink-0 items-center justify-center rounded-full transition-colors focus-visible:outline-none focus-visible:shadow-[var(--shadow-focus)] sm:size-9 ${
            isMessenger
              ? 'bg-white text-[#171c23] hover:bg-white/90'
              : 'bg-[var(--color-text)] text-[var(--color-surface)] hover:opacity-85'
          }`}
        >
          <PexIcon
            name={playing ? 'pause' : 'resume'}
            size="sm"
            decorative
            className="text-current"
          />
        </button>

        <div className="min-w-0 flex-1">
          <div
            className={`relative h-7 overflow-hidden rounded-full focus-within:shadow-[var(--shadow-focus)] ${
              isMessenger ? 'bg-white/10' : 'bg-[var(--color-surface-muted)]'
            }`}
          >
            <span
              aria-hidden="true"
              className={`absolute inset-y-1.5 left-0 rounded-full ${
                isMessenger ? 'bg-white/75' : 'bg-[var(--color-action)]'
              }`}
              style={{ width: `${progress}%` }}
            />
            <span
              aria-hidden="true"
              className={`absolute inset-x-1 inset-y-2 opacity-30 ${
                isMessenger ? 'text-white' : 'text-[var(--color-text-muted)]'
              }`}
              style={
                {
                  backgroundImage:
                    'repeating-linear-gradient(90deg, currentColor 0 1px, transparent 1px 5px)',
                } as CSSProperties
              }
            />
            <input
              type="range"
              min="0"
              max={Math.max(duration, 1)}
              step="0.1"
              value={Math.min(boundedCurrentTime, Math.max(duration, 1))}
              disabled={duration <= 0}
              aria-label={t.media.voiceProgress}
              aria-valuetext={`${formatTime(boundedCurrentTime)} / ${formatTime(duration)}`}
              onChange={(event) => seek(Number(event.currentTarget.value))}
              className="absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-default"
            />
          </div>
          <span
            className={`mt-0.5 block text-[10px] font-medium tabular-nums ${
              isMessenger
                ? 'text-white/55'
                : 'text-[var(--color-text-muted)]'
            }`}
          >
            {formatTime(boundedCurrentTime)} / {formatTime(duration)}
          </span>
        </div>

        {downloadUrl ? (
          <a
            href={downloadUrl}
            download
            aria-label={t.media.downloadVoiceMessage}
            className={`inline-flex size-11 shrink-0 items-center justify-center rounded-full transition-colors focus-visible:outline-none focus-visible:shadow-[var(--shadow-focus)] sm:size-9 ${
              isMessenger
                ? 'text-white/65 hover:bg-white/10 hover:text-white'
                : 'text-[var(--color-text-muted)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-text)]'
            }`}
          >
            <PexIcon
              name="download"
              size="sm"
              decorative
              className="text-current"
            />
          </a>
        ) : null}
      </div>

      {playbackFailed ? (
        <p
          role="alert"
          className={`mt-1.5 text-xs font-medium ${
            isMessenger ? 'text-red-300' : 'text-[var(--color-danger)]'
          }`}
        >
          {t.media.voicePlaybackFailed}
        </p>
      ) : null}

      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        className="sr-only"
        onLoadedMetadata={() => {
          syncDuration()
          markPlayable()
        }}
        onDurationChange={syncDuration}
        onCanPlay={markPlayable}
        onTimeUpdate={syncCurrentTime}
        onPlay={() => setPlaying(true)}
        onPlaying={markPlayable}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onError={() => {
          setPlaying(false)
          handleMediaError()
        }}
      />
    </div>
  )
}
